import React, { useState } from 'react';
import { View, Text, TextInput, TouchableOpacity, StyleSheet } from 'react-native';
import PropTypes from 'prop-types';
import DatePickerComponent from '../datepicker/DatePickerComponent';
import { Feather, Ionicons } from '@expo/vector-icons';
import getPlatformType from '../../helpers/helpers';

const layoutType = getPlatformType();

const TextInputComponent = (props) => {
  const {
    title,
    onChangeText,
    error,
    colored,
    fontSize,
    height,
    password,
    placeholder,
    value,
    disabled,
    isDate,
    date,
    onlyCalender,
    showMonths,
    multiline,
    renderRightComponent,
    allowDecimal,
    maxLength,
    isAmount,
    dateBackgroundColor,
    amountWidth,
    maxDate,
    isNumeric,
  } = props;

  const [passwordVisible, setPasswordVisible] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const onChangeTextCheck = (val) => {
    // ONLY NUMBERS (no alphabets)
    if (isNumeric) {
      const cleaned = val.replace(/[^0-9]/g, '');
      onChangeText && onChangeText(cleaned);
      return;
    }

    // AMOUNT or DECIMAL allowed
    if (isAmount || allowDecimal) {
      // Allow digits and ONE dot
      let cleaned = val.replace(/[^0-9.]/g, '');

      // Prevent entering more than one decimal point
      const parts = cleaned.split('.');
      if (parts.length > 2) {
        cleaned = parts[0] + '.' + parts[1];
      }

      onChangeText && onChangeText(cleaned);
      return;
    }

    // DEFAULT
    onChangeText && onChangeText(val);
  };

  const showPasswordToggle = password || renderRightComponent;

  return (
    <View style={[s.container, amountWidth ? amountWidth : null]}>
      {/* Label / Title */}
      {title ? (
        <Text style={[s.label, fontSize ? { fontSize: Number(fontSize) || 12 } : null]}>
          {title}
        </Text>
      ) : null}

      {/* DatePicker mode */}
      {isDate ? (
        <DatePickerComponent
          value={date}
          isDisable={disabled}
          showMonths={showMonths}
          onlyCalender={onlyCalender}
          backgroundColor={dateBackgroundColor}
          onUpdateInput={(d) => onChangeText && onChangeText(d)}
          minDate={props?.minDate}
          maxDate={maxDate}
        />
      ) : (
        /* Regular Input / Amount / Password mode */
        <View style={s.inputBoxWrap}>
          <View
            style={[
              s.inputContainer,
              colored && s.inputColored,
              disabled && s.inputDisabled,
              isFocused && s.inputFocused,
              !!error && s.inputError,
              multiline && { height: height ? height : 90, alignItems: 'flex-start' },
            ]}
          >
            {/* Currency Symbol Prefix */}
            {isAmount ? (
              <View style={[s.amountPrefix, multiline && { height: '100%' }]}>
                <Text style={s.amountText}>£</Text>
              </View>
            ) : null}

            {/* Main Text Input */}
            <TextInput
              style={[
                s.input,
                height ? { height: Number(height) || height } : null,
                multiline && s.inputMultiline,
              ]}
              placeholder={placeholder}
              placeholderTextColor="#9CA3AF"
              secureTextEntry={password && !passwordVisible}
              multiline={multiline ? true : false}
              value={value}
              onChangeText={onChangeTextCheck}
              editable={!disabled}
              selectTextOnFocus={!disabled}
              maxLength={maxLength ? maxLength : 500}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
            />

            {/* Password Eye Toggle / Right Icon */}
            {showPasswordToggle ? (
              <TouchableOpacity
                style={s.rightBtn}
                onPress={() => setPasswordVisible(!passwordVisible)}
                activeOpacity={0.7}
              >
                <Feather
                  name={passwordVisible ? 'eye' : 'eye-off'}
                  size={18}
                  color={passwordVisible ? '#6C2BD9' : '#9CA3AF'}
                />
              </TouchableOpacity>
            ) : null}
          </View>
        </View>
      )}

      {/* Error Message */}
      {error ? <Text style={s.errorText}>{error}</Text> : null}
    </View>
  );
};

const s = StyleSheet.create({
  container: {
    marginBottom: 14,
    width: '100%',
  },
  label: {
    fontSize: 13,
    fontWeight: '600',
    color: '#374151',
    marginBottom: 6,
  },
  inputBoxWrap: {
    width: '100%',
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#D1D5DB',
    borderRadius: 8,
    overflow: 'hidden',
  },
  inputColored: {
    backgroundColor: '#F5EEFF',
    borderColor: '#D8B4FE',
  },
  inputDisabled: {
    backgroundColor: '#F9FAFB',
    borderColor: '#E5E7EB',
  },
  inputFocused: {
    borderColor: '#6C2BD9',
    backgroundColor: '#FFFFFF',
  },
  inputError: {
    borderColor: '#EF4444',
  },
  amountPrefix: {
    width: 44,
    height: 44,
    backgroundColor: '#F3F4F6',
    borderRightWidth: 1,
    borderRightColor: '#D1D5DB',
    alignItems: 'center',
    justifyContent: 'center',
  },
  amountText: {
    fontSize: 15,
    fontWeight: '600',
    color: '#4B5563',
  },
  input: {
    flex: 1,
    height: 44,
    paddingHorizontal: 12,
    fontSize: 14,
    color: '#111827',
    fontWeight: '400',
  },
  inputMultiline: {
    height: '100%',
    paddingVertical: 10,
    textAlignVertical: 'top',
  },
  rightBtn: {
    paddingHorizontal: 12,
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorText: {
    fontSize: 12,
    color: '#EF4444',
    marginTop: 4,
    fontWeight: '500',
  },
});

TextInputComponent.propTypes = {
  title: PropTypes.string,
  onChangeText: PropTypes.func,
  error: PropTypes.string,
  colored: PropTypes.bool,
  fontSize: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  placeholder: PropTypes.string,
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  password: PropTypes.bool,
};

TextInputComponent.defaultProps = {
  disabled: false,
};

export default TextInputComponent;
